import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import Cropper from "cropperjs"

// Supporta due modalità:
//   1. Modal mode  (default): crop UI in un Bootstrap modal — per form normali
//   2. Panel mode: crop UI in un div inline espandibile — per form già dentro un modal
//
// Il controller rileva automaticamente la modalità cercando il target "panel" o "modal".

export default class extends Controller {
  static targets = ["input", "preview", "modal", "panel", "confirmBtn", "ratioBtn"]
  static values = {
    quality: { type: Number, default: 0.92 }
  }

  connect() {
    this.cropper = null
    this.modalInstance = null
    this._originalFileName = null
  }

  handleFileChange(event) {
    const file = event.target.files[0]
    if (!file) return

    this._originalFileName = file.name

    const reader = new FileReader()
    reader.onload = (e) => {
      this.previewTarget.src = e.target.result
      if (this.hasPanelTarget) {
        this._openPanel()
      } else {
        this._openModal()
      }
    }
    reader.readAsDataURL(file)
  }

  setRatio(event) {
    const ratio = parseFloat(event.params.ratio)
    if (this.cropper) {
      this.cropper.setAspectRatio(ratio > 0 ? ratio : NaN)
    }
    this.ratioBtnTargets.forEach(btn => {
      btn.classList.toggle("active", btn === event.currentTarget)
    })
  }

  confirm() {
    if (!this.cropper) return

    this.confirmBtnTarget.disabled = true
    this.confirmBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> ...'

    const canvas = this.cropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })

    canvas.toBlob((blob) => {
      const croppedFile = new File([blob], this._originalFileName || "photo.jpg", {
        type: "image/jpeg",
        lastModified: Date.now()
      })

      const dt = new DataTransfer()
      dt.items.add(croppedFile)
      this.inputTarget.files = dt.files

      if (this.hasPanelTarget) {
        this._closePanel()
      } else {
        this._closeModal()
      }
    }, "image/jpeg", this.qualityValue)
  }

  cancel() {
    this.inputTarget.value = ""
    if (this.hasPanelTarget) {
      this._closePanel()
    } else {
      this._closeModal()
    }
  }

  // --- Panel mode ---

  _openPanel() {
    this.panelTarget.style.display = "block"
    this.panelTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    // Due frame per garantire che il browser abbia calcolato il layout prima di Cropper.js
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._initCropper()
      })
    })
  }

  _closePanel() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    this.panelTarget.style.display = "none"
    this._resetConfirmBtn()
  }

  // --- Modal mode ---

  _openModal() {
    this.modalInstance = bootstrap.Modal.getOrCreateInstance(this.modalTarget)

    this.modalTarget.addEventListener("shown.bs.modal", () => {
      this._initCropper()
    }, { once: true })

    this.modalTarget.addEventListener("hidden.bs.modal", () => {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      this._resetConfirmBtn()
    }, { once: true })

    this.modalInstance.show()
  }

  _closeModal() {
    if (this.modalInstance) {
      this.modalInstance.hide()
    }
  }

  // --- Shared ---

  _initCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    const activeBtn = this.ratioBtnTargets.find(btn => btn.classList.contains("active"))
    const ratioVal = activeBtn ? parseFloat(activeBtn.dataset.imageCropRatioParam) : 0
    this.cropper = new Cropper(this.previewTarget, {
      aspectRatio: ratioVal > 0 ? ratioVal : NaN,
      viewMode: 1,
      autoCropArea: 0.85,
      responsive: true,
      background: true,
      movable: true,
      zoomable: true,
      rotatable: false
    })
  }

  _resetConfirmBtn() {
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = false
      this.confirmBtnTarget.innerHTML = '<i class="fa fa-check me-1"></i> Conferma ritaglio'
    }
  }

  disconnect() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }
}
